import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, BadgeCheck, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/smooth";

const lineV = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.13 },
  }),
};

export const Hero = ({ h1Lines, subtitle }) => {
  const bandRef = useRef(null);
  const videoRef = useRef(null);
  useEffect(() => {
    videoRef.current?.play().catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({ target: bandRef, offset: ["start end", "end start"] });
  const y2 = useTransform(scrollYProgress, [0, 1], ["45%", "-45%"]);
  const rot2 = useTransform(scrollYProgress, [0, 1], [7, -3]);

  return (
    <section className="relative overflow-hidden" data-testid="hero">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-xmove-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-40 h-[360px] w-[360px] rounded-full bg-xmove-orange/8 blur-3xl" />
      <div className="relative mx-auto max-w-3xl px-5 pb-12 pt-12 text-center md:pt-20 lg:pb-16">
        <h1 className="text-balance font-display text-4xl font-bold leading-[1.06] text-xmove-ink sm:text-5xl lg:text-6xl" data-testid="hero-h1">
          {h1Lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={lineV} initial="hidden" animate="visible" className="block">
                {i === h1Lines.length - 1 ? <span className="text-xmove-orange">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-xmove-muted md:text-lg"
          data-testid="hero-subtitle"
        >
          {subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-3"
          data-testid="hero-trust-bar"
        >
          <span className="flex items-center gap-2">
            <span className="flex" aria-label="5 stelle">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={17} className="fill-xmove-green text-xmove-green" />
              ))}
            </span>
            <span className="text-sm font-bold text-xmove-ink">5.0</span>
            <span className="text-sm text-xmove-muted">· Recensioni Google</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm text-xmove-muted">
            <BadgeCheck size={16} className="text-xmove-green" /> Risposta in pochi minuti
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-9"
        >
          <button
            onClick={() => scrollToId("preventivo")}
            data-testid="hero-cta-btn"
            className="group inline-flex items-center gap-2.5 rounded-full bg-xmove-orange px-8 py-4 font-display text-base font-bold text-white shadow-lift transition-all hover:bg-xmove-orangeDark active:scale-[0.98]"
          >
            Ricevi il preventivo gratuito
            <ArrowDown size={18} className="transition-transform group-hover:translate-y-0.5" />
          </button>
          <p className="mt-3 text-xs text-xmove-muted">Consulenza e preventivo sempre gratuiti</p>
        </motion.div>
      </div>

      <div ref={bandRef} className="relative mx-auto max-w-6xl px-5 pb-20 lg:px-8" data-testid="hero-photo-band">
        <div className="relative h-[260px] overflow-hidden rounded-2xl shadow-lift sm:h-[380px] lg:h-[480px]">
          <video
            ref={videoRef}
            poster="/images/racing-grid.webp"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
            data-testid="hero-video"
          >
            <source src="/images/hero-loop-web.mp4" type="video/mp4" />
            <source src="/images/hero-loop.webm" type="video/webm" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-xmove-night/35 via-transparent to-transparent" />
        </div>
        <motion.div
          style={{ y: y2, rotate: rot2 }}
          className="absolute -bottom-4 right-3 w-36 sm:right-10 sm:w-56 lg:w-72"
          data-testid="hero-racing-float"
        >
          <img
            src="/images/racing-track.webp"
            alt="Lamborghini Super Trofeo XMOVE #252 in azione in pista"
            loading="lazy"
            className="w-full rounded-xl border-4 border-white object-cover shadow-lift"
          />
        </motion.div>
      </div>
    </section>
  );
};
